import fs from "node:fs";
import path from "node:path";
import * as p from "@clack/prompts";
import type { AgentEvent } from "@skaile/workspaces/bridge";
import { loadFlow } from "@skaile/workspaces/factory-assets/connectors/flow/engine";
import { resumeFlow, runFlow } from "@skaile/workspaces/factory-assets/connectors/flow/run-flow";
import {
  clearSession,
  loadSession,
  loadSessionById,
  MarkdownStreamer,
  resolveSettings,
  runAgentChat,
} from "@skaile/workspaces/runner";
import { Command } from "commander";
import pc from "picocolors";
import { discoverFlows, findFlowFile } from "../flow-discovery.ts";
import { logErr, logInfo, logOk, logWarn, S } from "../helpers.ts";

/**
 * Returns an onEvent handler that streams markdown output and shows per-node
 * progress from state_changed events emitted by the FlowAdapter.
 */
function makeFlowEventHandler(): (event: AgentEvent) => void {
  const md = new MarkdownStreamer();
  const displayed = new Set<string>();

  return (event: AgentEvent) => {
    // Node progress from FlowAdapter state changes. Post-Phase-2 the store
    // ID is `flow:${runId}` (per-run namespace) and state.nodes is a
    // `Record<nodeId, NodeExecution>`, not the old array shape.
    const e = event as {
      type?: string;
      store?: string;
      state?: { nodes?: Record<string, { id: string; status: string }> };
    };
    if (e.type === "state_changed" && typeof e.store === "string" && e.store.startsWith("flow:")) {
      const nodes = e.state?.nodes ?? {};
      for (const id in nodes) {
        const n = nodes[id];
        const key = `${id}:${n.status}`;
        if (displayed.has(key)) continue;
        displayed.add(key);
        if (n.status === "running") {
          process.stdout.write(`  ${S.dim("→")} ${id}`);
        } else if (n.status === "complete") {
          process.stdout.write(` ${pc.green("✓")}\n`);
        } else if (n.status === "skipped") {
          process.stdout.write(` ${S.dim("↷ skipped")}\n`);
        }
      }
    }

    // Markdown streaming for agent text output
    if (event.type === "message_update") {
      const delta = (event as any)._textDelta;
      if (delta) md.write(delta);
    }
    if (event.type === "agent_end") md.end();
  };
}

/**
 * Create the `skaile run` command.
 *
 * Accepts a flow ID or a free-text prompt as positional arguments. When the
 * first positional arg matches a known flow ID (discovered from `ai-assets/`),
 * the flow is executed via {@link runFlow}. Otherwise all positional args are
 * joined and forwarded as a one-shot agent prompt via {@link runAgentChat}.
 * Supports `--dry-run`, `--project-dir`, `--driver`, `--model`, `--provider`,
 * `--label`, and `--skill` options. When called with no arguments an
 * interactive selection prompt is shown.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/run#make-run-command
 */
export function makeRunCommand(): Command {
  const cmd = new Command("run")
    .description("Start a flow or run a single-shot text prompt")
    .argument("[flow-id-or-text...]", "Flow ID or text prompt")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option("--driver <name>", "Driver backend (claude-sdk, codex, omp)")
    .option("--provider <name>", "LLM provider")
    .option("--model <name>", "Model override")
    .option("--label <label>", "Human-readable session label")
    .option("--dry-run", "Print plan without executing")
    .option("--skill <name>", "Skill name (for single-shot mode)")
    .action(async (positional: string[], opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const cliDriver = opts.driver as string | undefined;
      const dryRun = opts.dryRun ?? false;

      // Interactive flow selection if no args
      let flowId = positional[0];
      if (!flowId) {
        const flows = discoverFlows();
        if (flows.length === 0) {
          logErr("No flows available.");
          process.exit(1);
        }
        const selected = await p.select({
          message: "Select a flow to run",
          options: flows.map((f) => ({
            value: f.id,
            label: f.name,
            hint: f.description,
          })),
        });
        if (p.isCancel(selected)) {
          p.cancel("Cancelled.");
          process.exit(0);
        }
        flowId = selected as string;
      }

      // Resolve settings
      const config = await resolveSettings(projectDir, {
        driver: cliDriver,
        model: opts.model,
        provider: opts.provider,
      });
      const driver = config.driver ?? "omp";
      const model = config.model;
      const provider = config.provider;

      // Locate the flow file once: a project-local `ai-assets/` tree wins (lets
      // a workspace override a built-in flow), then fall back across all content
      // roots (bundled factory-assets, monorepo tree, user libraries). The file
      // path doubles as the is-this-a-flow test, so we don't parse every flow.
      // findFlowFile is layout-tolerant (flat + domain-nested, all extensions);
      // seed it with the project-local `ai-assets/` as a priority root.
      const priorityRoots: string[] = [];
      let dir = projectDir;
      for (let i = 0; i < 6; i++) {
        const candidate = path.join(dir, "ai-assets");
        if (fs.existsSync(candidate)) {
          priorityRoots.push(candidate);
          break;
        }
        dir = path.dirname(dir);
      }
      const flowPath = findFlowFile(flowId, priorityRoots);

      if (!flowPath) {
        // Not a flow id — treat the positional args as a single-shot text prompt.
        const textPrompt = positional.join(" ");
        if (!textPrompt.trim()) {
          logErr(`Flow not found: ${flowId}`);
          const available = discoverFlows().map((f) => f.id);
          if (available.length > 0) {
            console.log(`  ${S.dim("Available:")} ${available.map((id) => S.cmd(id)).join(", ")}`);
          }
          process.exit(1);
        }
        await runAgentChat({
          projectDir,
          driver: config.driver,
          model: config.model,
          provider: config.provider,
          message: textPrompt,
          skill: opts.skill,
        });
        return;
      }

      let flow: ReturnType<typeof loadFlow>;
      try {
        flow = loadFlow(flowPath);
      } catch (err) {
        logErr(
          `Failed to load flow "${flowId}": ${err instanceof Error ? err.message : String(err)}`,
        );
        process.exit(1);
      }

      console.log();
      console.log(`  ${S.label("Flow:")}        ${S.heading(flow.name)}`);
      console.log(`  ${S.label("Project dir:")} ${projectDir}`);
      console.log(`  ${S.label("Driver:")}      ${driver}${model ? S.dim(` / ${model}`) : ""}`);
      if (opts.label) console.log(`  ${S.label("Label:")}       ${opts.label}`);
      if (dryRun) console.log(`  ${S.warn("(dry run — no agent will be started)")}`);
      console.log();

      await runFlow({
        projectDir,
        flowPath,
        driver,
        model,
        provider,
        sessionLabel: opts.label,
        dryRun,
        onEvent: makeFlowEventHandler(),
        onLog: (line: string) => console.log(line),
      });
    });

  return cmd;
}

/**
 * Create the `skaile resume` command.
 *
 * Resumes the current or a specified session in `--project-dir`. Loads the
 * session pointer from `.skaile/session.json` (or a specific run-id via
 * `--session`) and delegates to {@link resumeFlow}. Supports `--dry-run` to
 * print the remaining plan without re-executing any agent steps.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/run#make-resume-command
 */
export function makeResumeCommand(): Command {
  const cmd = new Command("resume")
    .description("Resume current session")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option("--session <run-id>", "Target session by runId")
    .option("--dry-run", "Print plan without executing")
    .action(async (opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const sessionId = opts.session;
      const dryRun = opts.dryRun ?? false;

      const session = sessionId
        ? await loadSessionById(projectDir, sessionId)
        : await loadSession(projectDir);
      if (!session) {
        logErr(`No session in ${projectDir}`);
        console.log(`  ${S.dim("Run")} ${S.cmd("skaile run <flow-id>")} ${S.dim("first.")}`);
        process.exit(1);
      }

      console.log();
      console.log(
        `  ${S.label("Resuming:")} ${S.heading(session.flowId)}${session.label ? S.dim(` — ${session.label}`) : ""}`,
      );
      if (dryRun) logWarn("dry run");
      console.log();

      await resumeFlow({
        projectDir,
        sessionId,
        dryRun,
        onEvent: makeFlowEventHandler(),
        onLog: (line: string) => console.log(line),
      });
    });

  return cmd;
}

/**
 * Create the `skaile status` command.
 *
 * Reads the current session from `.skaile/session.json` in `--project-dir` and
 * prints a formatted summary: flow ID, run-id prefix, label, status (coloured),
 * driver/model, and age. Exits cleanly with an info message when no session
 * is active.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/run#make-status-command
 */
export function makeStatusCommand(): Command {
  return new Command("status")
    .description("Show current session state")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (opts) => {
      const projectDir = path.resolve(opts.projectDir);
      const session = await loadSession(projectDir);
      if (!session) {
        logInfo("No active session.");
        return;
      }

      const age = Math.round((Date.now() - new Date(session.updatedAt).getTime()) / 1000);
      const statusColor =
        session.status === "complete"
          ? pc.green
          : session.status === "failed"
            ? pc.red
            : session.status === "running"
              ? pc.cyan
              : pc.yellow;

      console.log();
      console.log(`  ${S.label("Flow:")}      ${S.heading(session.flowId)}`);
      console.log(`  ${S.label("Run ID:")}    ${session.runId.slice(0, 8)}`);
      if (session.label) console.log(`  ${S.label("Label:")}     ${session.label}`);
      console.log(`  ${S.label("Status:")}    ${statusColor(session.status ?? "unknown")}`);
      console.log(
        `  ${S.label("Driver:")}    ${session.driver}${session.model ? S.dim(` / ${session.model}`) : ""}`,
      );
      console.log(`  ${S.label("Updated:")}   ${session.updatedAt} ${S.dim(`(${age}s ago)`)}`);
      console.log(
        `  ${S.dim("Flow state is not persisted in CLI mode — use a host (Skaile platform, Pichi) for governed resumable runs.")}`,
      );
      console.log();
    });
}

/**
 * Create the `skaile clear` command.
 *
 * Removes the current session pointer from `.skaile/session.json` in
 * `--project-dir` via {@link clearSession}. Does not delete the underlying
 * session file — use `skaile session delete` for that.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/run#make-clear-command
 */
export function makeClearCommand(): Command {
  return new Command("clear")
    .description("Unset current session pointer")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (opts) => {
      const projectDir = path.resolve(opts.projectDir);
      await clearSession(projectDir);
      logOk("Session cleared.");
    });
}

/**
 * Create the `skaile repl` command.
 *
 * Starts an interactive REPL session via `@skaile/workspaces/tui`.
 *
 * Workspace resolution:
 *   1. Walk upward from CWD (or --project-dir) looking for skaile.yaml
 *   2. If found → that directory becomes the projectDir (config root);
 *      the user's CWD is forwarded as the agent working directory
 *   3. If not found → prompt the user to create a temporary workspace
 *      in /tmp; auto-detect backend (claude-sdk if ~/.claude exists, else omp)
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/repl#make-repl-command
 */
export function makeReplCommand(): Command {
  return new Command("repl")
    .description("Start an interactive agent session")
    .option("--project-dir <path>", "Project directory")
    .option("--driver <name>", "Driver backend (claude-sdk, omp)")
    .option("--provider <name>", "LLM provider")
    .option("--model <name>", "Model override")
    .option("--resume", "Resume last session")
    .option("--resume-session <id>", "Resume specific session")
    .action(async (opts) => {
      const { findWorkspaceRoot } = await import("@skaile/workspaces/core");
      const { startRepl } = await import("@skaile/workspaces/tui");
      const os = await import("node:os");

      const userCwd = path.resolve(opts.projectDir ?? process.cwd());
      const workspaceRoot = findWorkspaceRoot(userCwd);

      let projectDir: string;
      let agentCwd: string;
      let driverOverride = opts.driver as string | undefined;

      if (workspaceRoot) {
        // Found skaile.yaml — use its directory as config root
        projectDir = workspaceRoot;
        agentCwd = userCwd;
        if (workspaceRoot !== userCwd) {
          logInfo(`Using workspace: ${S.cmd(workspaceRoot)}`);
        }
      } else {
        // No skaile.yaml found anywhere
        console.log();
        logWarn(`No skaile.yaml found in ${S.cmd(userCwd)} or any parent directory.`);
        console.log();

        const shouldCreate = await p.confirm({
          message: "Create a temporary workspace? (session state in /tmp/)",
        });
        if (p.isCancel(shouldCreate) || !shouldCreate) {
          p.cancel("Cancelled.");
          process.exit(0);
        }

        // Create temp workspace
        const tmpDir = fs.mkdtempSync(path.join(os.tmpdir(), "skaile-repl-"));
        projectDir = tmpDir;
        agentCwd = userCwd;
        logOk(`Temporary workspace: ${S.cmd(tmpDir)}`);

        // Auto-detect backend if no --driver given
        if (!driverOverride) {
          const claudeDir = path.join(os.homedir(), ".claude");
          if (fs.existsSync(claudeDir)) {
            driverOverride = "claude-sdk";
            logInfo(`Detected ${S.cmd("~/.claude")} → using ${S.cmd("claude-sdk")} backend`);
          } else {
            driverOverride = "omp";
            logInfo(`Using ${S.cmd("omp")} backend`);
          }
        }

        console.log();
      }

      await startRepl({
        projectDir,
        cwd: agentCwd !== projectDir ? agentCwd : undefined,
        driver: driverOverride,
        model: opts.model,
        provider: opts.provider,
        resume: opts.resume,
        resumeSessionId: opts.resumeSession,
      });
    });
}
